<template>
  <div>
    <distinguished-game v-if="isGameInProgress" @game:over="handleGameOver"></distinguished-game>
    <main v-else class="gameplay"></main>

    <base-loader-modal v-if="isLoaderModalOpen"></base-loader-modal>

    <pre-game-modal
      v-if="isPreGameModalOpen"
      @close:modal="handleClosePreGameModal"
    ></pre-game-modal>

    <post-game-modal
      v-if="isPostGameModalOpen"
      :post-game-info="postGameInfo"
      @game:new="handleNewGameFromPostGameModal"
      @redirect:storePage="handleGoToStorePageFromPostGameModal"
      @redirect:homePage="handleGoToHomePageFromPostGameModal"
    ></post-game-modal>

    <game-maintenance
      v-if="isMaintenanceModalOpen"
      @redirect:page="handlePageRedirectionFromMaintenanceModal"
    ></game-maintenance>
  </div>
</template>

<script>
import {mapState, mapMutations, mapActions} from 'vuex'
import BaseLoaderModal from '../../common/modal/BaseLoaderModal'
import PreGameModal from '../modals/pregame/PreGameModal'
import PostGameModal from '../modals/postgame/PostGameModal'
import GameMaintenance from '../modals/Maintenance'
import GameHeader from './GameHeader'
import Timer from './Timer'
import DistinguishedGame from './DistinguishedGame'
import {ParseResponseMixin} from '../../../common/mixins/game'
import auth from '../../../common/auth'
import modals from '../../../common/modals'
import webSocket from '../../../common/webSocket'
import {isNumber} from '../../../helpers/common'
import {removeLocalStorageValueByUser, setLocalStorageValueByUser} from '../../../helpers/user'

export default {
  name: 'genericGameComponent',
  mixins: [ParseResponseMixin],
  components: {
    BaseLoaderModal,
    PreGameModal,
    PostGameModal,
    GameMaintenance,
    GameHeader,
    Timer,
    DistinguishedGame
  },
  data () {
    return {
      isMusicOnFromSettings: localStorage.getItem('musicOn') !== 'false',
      stompClient: null,
      socketChannels: {fetchAllGameData: null, finishGame: null},
      isPreGameModalOpen: false,
      isLoaderModalOpen: false,
      isPostGameModalOpen: false,
      isMaintenanceModalOpen: false,
      isGameInProgress: false,
      postGameInfo: {}
    }
  },
  created () {
    const isPreGameCompleted = JSON.parse(localStorage.getItem('isPreGameCompleted'))

    localStorage.removeItem('isPreGameCompleted')

    if (this.isMusicOnFromSettings) {
      this.$updateMusicState(false)
    }

    if (isPreGameCompleted) {
      setLocalStorageValueByUser('isGameInProgress', this.userDetails.msisdn, true)

      this.initializeGame()
    } else {
      this.goToHomePage()
    }
  },
  beforeDestroy () {
    this.unsubscribeFromFetchAllGameDataChannel()
    this.unsubscribeFromFinishGameChannel()
    this.disconnectGameStompClient()

    if (this.isMusicOnFromSettings) {
      this.$updateMusicState(true)
    }
  },
  methods: {
    ...mapActions('profile', [
      'getUserLoginDetails'
    ]),
    ...mapMutations('game', [
      'setGameID',
      'setMiniGames'
    ]),
    stopLoaderAndStartGame () {
      modals.hideCommonModal({
        afterHideCallback: () => {
          this.isLoaderModalOpen = false
          this.isGameInProgress = true
        }
      })
    },
    stopLoaderAndShowMaintenanceModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isLoaderModalOpen = false

          this.isMaintenanceModalOpen = true

          modals.setupCommonModal({
            hideImmediate: true,
            afterHideCallback: () => {
              this.isMaintenanceModalOpen = false

              this.clearIsGameInProgressValue()
              this.goToHomePage()
            }
          })
        }
      })
    },
    async initializeGame () {
      this.isLoaderModalOpen = true
      modals.setupCommonModal({showImmediate: true, hideManually: true})

      // should check the 'gameType'

      this.initializeSocket()

      const connectInfo = await this.connectToSocket()

      if (!connectInfo.success) {
        this.stopLoaderAndShowMaintenanceModal()

        return
      }

      const fetchGameDataInfo = await this.subscribeToFetchAllGameDataChannel()

      if (!fetchGameDataInfo.success) {
        this.stopLoaderAndShowMaintenanceModal()

        return
      }

      this.stopLoaderAndStartGame()

      this.subscribeToFinishGameChannel()
    },
    createGameStompClient () {
      this.stompClient = webSocket.createNewStompClient('/gameData')
    },
    async disconnectGameStompClient () {
      if (this.stompClient) {
        await webSocket.disconnectStompClient(this.stompClient)
      }
    },
    initializeSocket () {
      this.stompClient = webSocket.getGameStompClient()

      if (this.stompClient === null || !this.stompClient.connected) {
        this.createGameStompClient()
      }
    },
    connectToSocket () {
      return new Promise(resolve => {
        if (this.stompClient.connected) {
          return resolve({success: true})
        }

        let connectTimeoutId = null

        const connect = selfRecursiveCallback =>
          new Promise((connectResolve, connectReject) => {
            this.stompClient.connect(
              {Authorization: auth.getJwt()},
              connectResolve,
              connectReject
            )
          }).then(() => {
            if (connectTimeoutId) { clearTimeout(connectTimeoutId) }

            resolve({success: true})
          }).catch(() => {
            connectTimeoutId = setTimeout(() => {
              this.createGameStompClient()

              selfRecursiveCallback(selfRecursiveCallback)
            }, 3000)
          })

        connect(connect)
      })
    },
    subscribeToFetchAllGameDataChannel () {
      return new Promise(resolve => {
        this.socketChannels.fetchAllGameData = this.stompClient.subscribe(`/topic/ctasFetched/${this.userDetails.msisdn}`, response => {
          this.unsubscribeFromFetchAllGameDataChannel()

          const responseData = this.parseServerResponse(response.body)

          if (responseData === null || responseData === 'Maintenance') {
            return resolve({success: false})
          }

          this.setGameID(responseData.id)
          this.setMiniGames(responseData.miniGames.map(miniGame => ({...miniGame, userAnswerInfo: {text: '', choice: ''}})))
          //   this.setGameType(this.gameType)
          //   this.updateGameMode(this.gameMode)

          resolve({success: true})
        }, error => {
          console.error(`Error response: ${error}`)
          resolve({success: false})
        })
      })
    },
    subscribeToFinishGameChannel () {
      this.socketChannels.finishGame = this.stompClient.subscribe(`/topic/finishedGame/${this.gameID}`, async response => {
        this.isGameInProgress = false

        if (!this.isLoaderModalOpen) {
          this.isLoaderModalOpen = true
          modals.setupCommonModal({hideManually: true})
        }

        this.unsubscribeFromFinishGameChannel()

        await this.getUserLoginDetails()

        const finishGameResponse = this.parseServerResponse(response.body)

        if (finishGameResponse === null || finishGameResponse === 'Maintenance') {
          this.stopLoaderAndShowMaintenanceModal()
        } else {
          this.openPostGameModal(finishGameResponse)
        }
      }, error => {
        console.error(`Finish Game: Error response: ${error}`)
      })
    },
    unsubscribeFromFetchAllGameDataChannel () {
      if (this.socketChannels.fetchAllGameData) {
        this.socketChannels.fetchAllGameData.unsubscribe()
        this.socketChannels.fetchAllGameData = null
      }
    },
    unsubscribeFromFinishGameChannel () {
      if (this.socketChannels.finishGame) {
        this.socketChannels.finishGame.unsubscribe()
        this.socketChannels.finishGame = null
      }
    },
    handleClosePreGameModal ({canProceed}) {
      this.isPreGameModalOpen = false

      if (canProceed) {
        this.unsubscribeFromFetchAllGameDataChannel()
        this.unsubscribeFromFinishGameChannel()

        this.initializeGame()
      } else {
        this.clearIsGameInProgressValue()
      }
    },
    async handleGameOver ({gameCompleted}) {
      if (this.isGameInProgress) {
        this.isLoaderModalOpen = true
        modals.setupCommonModal({hideManually: true})

        if (gameCompleted) {
          try {
            // after next call, we will receive a message through socket, implemented in 'subscribeToFinishGameChannel' function
            await this.$axios({
              url: `api/sendFinishDistinguished`,
              method: 'GET',
              headers: {Authorization: auth.getJwt()},
              params: {gameId: this.gameID}
            })
          } catch (_e) {
            this.unsubscribeFromFinishGameChannel()
            this.stopLoaderAndShowMaintenanceModal()
          }
        }
      }
    },
    openPostGameModal (finishGameResponse) {
      const totalQuestions = Array.isArray(finishGameResponse.listOfQuestions)
        ? finishGameResponse.listOfQuestions.length
        : 0
      const correctAnswers = isNumber(finishGameResponse.correctQuestions)
        ? finishGameResponse.correctQuestions
        : 0

      this.postGameInfo = {
        totalQuestions,
        correctAnswers,
        wrongAnswers: (totalQuestions > correctAnswers)
          ? totalQuestions - correctAnswers
          : 0,
        correctAnswersPercent: totalQuestions > 0
          ? Math.floor((correctAnswers / totalQuestions) * 100)
          : 0,
        pointsEarned: (finishGameResponse.userGameDetailsDTO && isNumber(finishGameResponse.userGameDetailsDTO.postgamePointsTotal))
          ? finishGameResponse.userGameDetailsDTO.postgamePointsTotal
          : 0,
        currentLevel: this.userDetails.level,
        remainingPointsToLevel: this.userDetails.remainingPointsToLevel,
        availableGames: this.userDetails.availableGames
      }

      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isLoaderModalOpen = false

          this.isPostGameModalOpen = true

          modals.setupCommonModal({
            hideImmediate: true,
            afterHideCallback: () => {
              this.isPostGameModalOpen = false

              this.clearIsGameInProgressValue()
              this.goToHomePage()
            }
          })
        }
      })
    },
    handleNewGameFromPostGameModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isPostGameModalOpen = false
          this.postGameInfo = {}

          this.isPreGameModalOpen = true
        }
      })
    },
    handleGoToStorePageFromPostGameModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isPostGameModalOpen = false

          this.clearIsGameInProgressValue()
          this.goToStorePage()
        }
      })
    },
    handleGoToHomePageFromPostGameModal () {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isPostGameModalOpen = false

          this.clearIsGameInProgressValue()
          this.goToHomePage()
        }
      })
    },
    handlePageRedirectionFromMaintenanceModal ({pagePath}) {
      modals.hideCommonModal({
        immediate: true,
        afterHideCallback: () => {
          this.isMaintenanceModalOpen = false

          this.clearIsGameInProgressValue()
          this.$router.push(pagePath)
        }
      })
    },
    clearIsGameInProgressValue () {
      removeLocalStorageValueByUser('isGameInProgress', this.userDetails.msisdn)
    },
    goToHomePage () {
      this.$router.push('/home')
    },
    goToStorePage () {
      this.$router.push('/home/store')
    }
  },
  computed: {
    ...mapState('game', [
      'gameID',
      'gameType',
      'gameMode'
    ]),
    ...mapState('common', [
      'userDetails'
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/home/game/Game.vue